import { useState, useCallback } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";

import useEntitlements from "@/auth/useEntitlements";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import {
    FEATURE_ACCOUNTING_STRATEGY,
    FEATURE_PROJECT_OWNER,
    FEATURE_MULTI_LEGAL_ENTITY,
    FEATURE_MULTI_PERSON,
    FEATURE_PROJECT_FORECAST,
} from "@/constants";

import {
    queryToFilterValue,
    dateQueryToFilterValue,
    statusQueryToFilterValue,
} from "@/utils";

const ProjectSliderGrid = ({ value, label, handleAmountChanges, id }) => {
    const maxValue = 1000000;

    const [newValue, setNewValue] = useState(value);

    const handleChange = (event, newValue) => {
        setNewValue(newValue);
    };

    const handleChangeCommitted = useCallback(
        (event, newValue) => {
            const timeout = setTimeout(() => {
                handleAmountChanges(id)(event, newValue);
            }, 500);

            return () => clearTimeout(timeout);
        },
        [handleAmountChanges, id],
    );

    return (
        <Grid item xs={6} md={2}>
            <Typography gutterBottom>{label}</Typography>
            <Slider
                step={1000}
                value={newValue}
                min={0}
                max={maxValue}
                onChange={handleChange}
                onChangeCommitted={handleChangeCommitted}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => {
                    return value.toLocaleString();
                }}
                sx={{ margin: "0px 8px" }}
            />
        </Grid>
    );
};

export default function ProjectFilters(props) {
    const {
        filters,
        setFilters,
        handleInputChange,
        handleAutocompleteChange,
        handleAutocompleteClear,
    } = props;
    const { hasFeature } = useEntitlements();

    const handleMultiAutoCompleteChange = (filterName) => (event, newValue) => {
        if (newValue && newValue.length > 0) {
            setFilters({
                ...filters,
                [filterName]: "in:" + newValue.join(","),
            });
        }
    };
    const handleAmountChanges = (filterName) => (event, newValue) => {
        setFilters({
            ...filters,
            [filterName]: ["gte:" + newValue[0], "lte:" + newValue[1]],
        });
    };
    const handleCalendarChange = (filterName) => (newValue) => {
        if (new Date(newValue) != "Invalid Date" && newValue != null) {
            setFilters({
                ...filters,
                [filterName]: "gte:" + format(newValue, "y-MM-dd"),
            });
        } else if (newValue == null) {
            setFilters({ ...filters, [filterName]: null });
        }
    };

    return (
        <>
            <Grid container sx={{ paddingTop: 2 }} spacing={3}>
                <Grid item xs={6} md={2}>
                    <TextField
                        value={queryToFilterValue(filters.docket) || ""}
                        sx={{ width: "100%" }}
                        onChange={handleInputChange("docket")}
                        label="Docket"
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={6} md={2}>
                    <TextField
                        value={queryToFilterValue(filters.name) || ""}
                        sx={{ width: "100%" }}
                        onChange={handleInputChange("name")}
                        label="Name"
                        variant="outlined"
                    />
                </Grid>
                {hasFeature(FEATURE_PROJECT_OWNER) && (
                    <Grid item xs={6} md={2}>
                        <ApiAutocomplete
                            silentErrors
                            value={queryToFilterValue(filters.owner_id)}
                            apiRoute={"persons"}
                            fields={["first_name", "last_name"]}
                            onChange={handleAutocompleteChange("owner_id")}
                            onInputChange={handleAutocompleteClear("owner_id")}
                            label={"Owner"}
                        />
                    </Grid>
                )}
                {hasFeature(FEATURE_MULTI_PERSON) && (
                    <Grid item xs={6} md={2}>
                        <ApiAutocomplete
                            silentErrors
                            value={queryToFilterValue(filters.person_id)}
                            apiRoute={"persons"}
                            fields={["first_name", "last_name"]}
                            onChange={handleAutocompleteChange("person_id")}
                            onInputChange={handleAutocompleteClear("person_id")}
                            label={"Producer"}
                        />
                    </Grid>
                )}
                <Grid item xs={6} md={2}>
                    <ApiAutocomplete
                        silentErrors
                        value={queryToFilterValue(filters.client_id)}
                        apiRoute={"clients"}
                        fields={["name"]}
                        onChange={handleAutocompleteChange("client_id")}
                        onInputChange={handleAutocompleteClear("client_id")}
                        label={"Client"}
                    />
                </Grid>
                {hasFeature(FEATURE_MULTI_LEGAL_ENTITY) && (
                    <Grid item xs={6} md={2}>
                        <ApiAutocomplete
                            silentErrors
                            value={queryToFilterValue(filters.legal_entity_id)}
                            apiRoute={"legal-entities"}
                            fields={["name"]}
                            onChange={handleAutocompleteChange(
                                "legal_entity_id",
                            )}
                            onInputChange={handleAutocompleteClear(
                                "legal_entity_id",
                            )}
                            label={"Entity"}
                        />
                    </Grid>
                )}
                <Grid item xs={6} md={4}>
                    <ApiAutocomplete
                        silentErrors
                        value={statusQueryToFilterValue(filters.status_id)}
                        apiRoute={"statuses"}
                        fields={["name"]}
                        onChange={handleMultiAutoCompleteChange("status_id")}
                        onInputChange={handleAutocompleteClear("status_id")}
                        isOptionEqualToValue={(option, values) =>
                            values.includes(option)
                        }
                        label={"Status"}
                        multiple
                    />
                </Grid>
                <Grid item xs={6} md={2}>
                    <Autocomplete
                        value={queryToFilterValue(filters.signed)}
                        disablePortal
                        options={["true", "false"]}
                        getOptionLabel={(option) =>
                            option == "true" ? "yes" : "no"
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Signed" />
                        )}
                        onChange={handleAutocompleteChange("signed")}
                        onInputChange={handleAutocompleteClear("signed")}
                    />
                </Grid>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid item xs={6} md={2}>
                        <DatePicker
                            value={dateQueryToFilterValue(filters.start_date)}
                            label="Start Date is After"
                            onChange={handleCalendarChange("start_date")}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <DatePicker
                            value={dateQueryToFilterValue(filters.end_date)}
                            label="End Date is After"
                            onChange={handleCalendarChange("end_date")}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <DatePicker
                            value={dateQueryToFilterValue(filters.live_date)}
                            label="Live Date is After"
                            onChange={handleCalendarChange("live_date")}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                </LocalizationProvider>
                {hasFeature(FEATURE_PROJECT_FORECAST) && (
                    <ProjectSliderGrid
                        value={
                            filters.forecast_revenue
                                ? [
                                      +queryToFilterValue(
                                          filters.forecast_revenue[0],
                                      ),
                                      +queryToFilterValue(
                                          filters.forecast_revenue[1],
                                      ),
                                  ]
                                : [0, 0]
                        }
                        handleAmountChanges={handleAmountChanges}
                        label={"Rev. Forecast"}
                        id={"forecast_revenue"}
                    />
                )}
                <ProjectSliderGrid
                    value={
                        filters.bid_quoted
                            ? [
                                  +queryToFilterValue(filters.bid_quoted[0]),
                                  +queryToFilterValue(filters.bid_quoted[1]),
                              ]
                            : [0, 0]
                    }
                    handleAmountChanges={handleAmountChanges}
                    label={"Quoted"}
                    id={"bid_quoted"}
                />
                <ProjectSliderGrid
                    value={
                        filters.bid_hard_cost
                            ? [
                                  +queryToFilterValue(filters.bid_hard_cost[0]),
                                  +queryToFilterValue(filters.bid_hard_cost[1]),
                              ]
                            : [0, 0]
                    }
                    handleAmountChanges={handleAmountChanges}
                    label={"Hard Costs"}
                    id={"bid_hard_cost"}
                />
                <ProjectSliderGrid
                    value={
                        filters.bid_gp
                            ? [
                                  +queryToFilterValue(filters.bid_gp[0]),
                                  +queryToFilterValue(filters.bid_gp[1]),
                              ]
                            : [0, 0]
                    }
                    handleAmountChanges={handleAmountChanges}
                    label={"Gross Profit"}
                    id={"bid_gp"}
                />
                {hasFeature(FEATURE_ACCOUNTING_STRATEGY) && (
                    <>
                        <ProjectSliderGrid
                            value={
                                filters.accounting_rev
                                    ? [
                                          +queryToFilterValue(
                                              filters.accounting_rev[0],
                                          ),
                                          +queryToFilterValue(
                                              filters.accounting_rev[1],
                                          ),
                                      ]
                                    : [0, 0]
                            }
                            handleAmountChanges={handleAmountChanges}
                            label={"BILLED (QB)"}
                            id={"accounting_rev"}
                        />
                        <ProjectSliderGrid
                            value={
                                filters.accounting_cost
                                    ? [
                                          +queryToFilterValue(
                                              filters.accounting_cost[0],
                                          ),
                                          +queryToFilterValue(
                                              filters.accounting_cost[1],
                                          ),
                                      ]
                                    : [0, 0]
                            }
                            handleAmountChanges={handleAmountChanges}
                            label={"SPENT (QB)"}
                            id={"accounting_cost"}
                        />
                    </>
                )}
            </Grid>
        </>
    );
}
